import { ColumnDef } from "@tanstack/react-table";
import {
  CrossCircleIcon,
  CrossInRedCircle,
  EyeIcon,
  ReplaceIcon,
  TrashAltIcon
} from "assets/icons";
import { t } from "i18next";
import { useProfile, StaffManagerPermissions } from "modules/common";
/* eslint-disable react-hooks/exhaustive-deps */
import moment from "moment";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, ItemsTable, PersonInfo, TableItemActionsControl } from "ui";

import { getResellerLevelLabelIcon } from "../../helpers";
import { ResellersListItem } from "../../resellers.types";
import { ResellerVerificationStatusLabel } from "../ResellerVerificationStatusLabel";
import { ResellersTableProps } from "./ResellersTable.types";

import styles from "./ResellersTable.module.scss";

const ResellersTable = ({
  items,
  sorting,
  setSorting,
  isLoading,
  onChangeDeleteStatus,
  onChangeBlockStatus
}: ResellersTableProps) => {
  const navigate = useNavigate();

  const { user } = useProfile({ enabled: false });

  const { canBlock, canUnblock, canDelete, canRestore } = useMemo(() => {
    return {
      canBlock:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.BlockResellers),
      canUnblock:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.UnblockResellers),
      canDelete:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.DeleteResellers),
      canRestore:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.RestoreResellers)
    };
  }, [user]);

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const columns: Array<ColumnDef<ResellersListItem, ResellersListItem[]>> =
    useMemo(
      () => [
        {
          id: "FullName",
          accessorKey: "FullName",
          header: t("Reseller"),
          size: 25,
          minSize: 25,
          cell: ({ row }) => {
            const levelIcon = getResellerLevelLabelIcon(row.original.level);

            return (
              <PersonInfo
                image={row.original.image}
                personName={`${row.original.firstname} ${row.original.lastname}`}
                nameMarker={
                  <>
                    <span className={styles.resellersTable_blockedLabel}>
                      {row.original.isBlocked ? " 🚫" : ""}
                    </span>
                    {levelIcon && <Icon as={levelIcon} />}
                  </>
                }
                nameClassName={styles.resellersTable_name}
                bottomText={[row.original.gender && t(row.original.gender), `${row.original.age} y.o.`]
                  .filter(Boolean)
                  .join(" · ")}
                imageMarker={
                  row.original.isDeleted ? (
                    <Icon as={CrossInRedCircle} width={18} height={18} />
                  ) : (
                    <ResellerVerificationStatusLabel
                      onlyIcon
                      status={row.original.verificationStatus}
                    />
                  )
                }
              />
            );
          }
        },
        {
          id: "Location",
          accessorKey: "Location",
          header: t("Location"),
          size: 18,
          minSize: 18,
          cell: ({ row }) =>
            row.original.country || row.original.city ? (
              <div>{`${row.original.country}, ${row.original.city} `}</div>
            ) : (
              t("Unknown location")
            )
        },
        {
          id: "NumberOfCanceledOrders",
          accessorKey: "NumberOfCanceledOrders",
          header: t("Canceled"),
          size: 4,
          minSize: 4,
          cell: ({ row }) => row.original.numberOfCanceledOrders
        },
        {
          id: "NumberOfDeliveredOrders",
          accessorKey: "NumberOfDeliveredOrders",
          header: t("Delivered"),
          size: 5,
          minSize: 5,
          cell: ({ row }) => row.original.numberOfDeliveredOrders
        },
        {
          id: "NumberOfReturnedOrders",
          accessorKey: "NumberOfReturnedOrders",
          header: t("Returned"),
          size: 5,
          minSize: 5,
          cell: ({ row }) => row.original.numberOfReturnedOrders
        },

        {
          id: "TotalSales",
          accessorKey: "TotalSales",
          header: `${t("Sales")} (FCFA)`,
          size: 18,
          minSize: 18,
          cell: ({ row }) => row.original.totalSales.toLocaleString("en-US")
        },
        {
          id: "TotalEarnings",
          accessorKey: "TotalEarnings",
          header: t("Earnings"),
          size: 9,
          minSize: 9,
          cell: ({ row }) => row.original.totalEarnings.toLocaleString("en-US")
        },
        {
          id: "RegistrationDate",
          accessorKey: "RegistrationDate",
          header: t("Registration date"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => (
            <pre>
              {`${moment(row.original.createdAt).format("DD/MM/YYYY")}`}
              {"\n"}
              {`${moment(row.original.createdAt).format("HH:mm")}`}
            </pre>
          )
        },
        {
          accessorKey: "dotsColumn",
          header: () => null,
          size: 3,
          minSize: 3,
          cell: ({ row }) => {
            const daysLeftToRestore = row.original.accountRestorationDeadline
              ? Math.ceil(
                  moment(row.original.accountRestorationDeadline).diff(
                    moment(),
                    "days",
                    true
                  )
                )
              : 0;
            return (
              <TableItemActionsControl
                actionsList={[
                  {
                    label: t("View"),
                    Icon: <EyeIcon fill={"#090914"} width={18} height={18} />,
                    onClick: () => {
                      navigate(`./${row.original.id}/mainInfo`, {
                        state: window.location.search
                      });
                    }
                  },
                  ...(row.original.isDeleted
                    ? []
                    : row.original.isBlocked && canUnblock
                      ? [
                          {
                            label: t("Unblock"),
                            Icon: (
                              <CrossCircleIcon
                                fill={"#090914"}
                                width={18}
                                height={18}
                              />
                            ),
                            onClick: () => onChangeBlockStatus(row.original)
                          }
                        ]
                      : !row.original.isBlocked && canBlock
                        ? [
                            {
                              label: t("Block"),
                              Icon: (
                                <CrossCircleIcon
                                  fill={"#090914"}
                                  width={18}
                                  height={18}
                                />
                              ),
                              onClick: () => onChangeBlockStatus(row.original)
                            }
                          ]
                        : []),

                  ...(!row.original.isDeleted && canDelete
                    ? [
                        {
                          label: t("Delete"),
                          Icon: (
                            <TrashAltIcon
                              fill={"#ff0033"}
                              width={18}
                              height={18}
                            />
                          ),
                          labelColor: "#ff0033",
                          onClick: () => onChangeDeleteStatus(row.original)
                        }
                      ]
                    : row.original.isDeleted &&
                        canRestore &&
                        daysLeftToRestore > 0
                      ? [
                          {
                            label: t("Restore"),
                            Icon: (
                              <ReplaceIcon
                                fill={"#090914"}
                                width={17}
                                height={17}
                              />
                            ),
                            onClick: () => onChangeDeleteStatus(row.original)
                          }
                        ]
                      : [])
                ]}
                rowIndex={row.index}
                setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
                openedItemOptionsIndex={openedItemOptionsIndex}
              />
            );
          },
          enableSorting: false
        }
      ],
      [items, user, openedItemOptionsIndex]
    );

  return (
    <>
      <ItemsTable
        className={styles.resellersTable}
        columns={columns}
        data={items}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        skeletonHeight={65}
        getLinkUrl={({ id }) => `./${id}/mainInfo`}
        linkState={window.location.search}
      />
    </>
  );
};

export default ResellersTable;
